<!-- src/apps/secret/views/docs/ApiReference.vue -->

<script setup lang="ts">
  /**
   * Public API reference (/docs).
   *
   * On-brand, dependency-free renderer for the project's generated OpenAPI 3.1
   * specs — no Redoc/Scalar/Swagger-UI (which would drag in heavy bundles and
   * fight our strict CSP). The three PUBLIC specs (v1/v2/v3) are served as
   * static files at /openapi/openapi.v{1,2,3}.json and fetched on demand when a
   * version tab is opened; the JSON is never imported into the SPA bundle. The
   * privileged internal spec is neither served nor referenced here.
   *
   * connect-src 'self' permits these same-origin fetches.
   */
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import ApiEndpoint from './ApiEndpoint.vue';

  const { t } = useI18n();

  type VersionId = 'v2' | 'v3' | 'v1';
  type StatusKey = 'stable' | 'alpha' | 'legacy';

  interface VersionMeta {
    id: VersionId;
    url: string;
    status: StatusKey;
  }

  // Tab order: v2 (stable, default) · v3 (alpha) · v1 (legacy).
  const VERSIONS: VersionMeta[] = [
    { id: 'v2', url: '/openapi/openapi.v2.json', status: 'stable' },
    { id: 'v3', url: '/openapi/openapi.v3.json', status: 'alpha' },
    { id: 'v1', url: '/openapi/openapi.v1.json', status: 'legacy' },
  ];

  type OpenApiOperation = Record<string, unknown>;
  interface OpenApiDoc {
    info?: { title?: string; description?: string; version?: string };
    servers?: Array<{ url?: string; description?: string }>;
    paths?: Record<string, Record<string, OpenApiOperation>>;
  }

  interface SpecState {
    status: 'idle' | 'loading' | 'loaded' | 'error';
    doc?: OpenApiDoc;
  }

  const specs = reactive<Record<VersionId, SpecState>>({
    v2: { status: 'idle' },
    v3: { status: 'idle' },
    v1: { status: 'idle' },
  });

  const selected = ref<VersionId>('v2');

  const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete'];
  const VERSION_SEG = /^v\d+$/;

  async function loadSpec(id: VersionId) {
    const state = specs[id];
    if (state.status === 'loaded' || state.status === 'loading') return;
    const meta = VERSIONS.find((v) => v.id === id);
    if (!meta) return;

    state.status = 'loading';
    try {
      const res = await fetch(meta.url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      state.doc = (await res.json()) as OpenApiDoc;
      state.status = 'loaded';
    } catch (err) {
      console.warn(`[docs] Failed to load ${id} spec`, err);
      state.status = 'error';
    }
  }

  function selectVersion(id: VersionId) {
    selected.value = id;
    void loadSpec(id);
  }

  const current = computed(() => specs[selected.value]);
  const currentMeta = computed(
    () => VERSIONS.find((v) => v.id === selected.value) as VersionMeta
  );
  const currentUrl = computed(() => currentMeta.value.url);

  const baseUrl = computed(() => {
    const servers = current.value.doc?.servers;
    return servers?.[0]?.url ?? 'https://api.seuid.com.br';
  });

  const statusLabel = (status: StatusKey) => t(`web.docs.status_${status}`);

  /** Group endpoints by the resource segment following the version/mount. */
  function groupKey(path: string): string {
    const parts = path.split('/').filter(Boolean); // ['api','v2','receipt','recent']
    if (parts[0] !== 'api') return parts[0] ?? 'other';
    const mount = parts[1] ?? '';
    if (!VERSION_SEG.test(mount)) return mount; // e.g. 'incoming'
    const resource = parts[2];
    if (!resource || resource.startsWith('{')) return mount;
    return resource;
  }

  interface EndpointEntry {
    path: string;
    method: string;
    operation: OpenApiOperation;
  }
  interface EndpointGroup {
    name: string;
    endpoints: EndpointEntry[];
  }

  const groups = computed<EndpointGroup[]>(() => {
    const doc = current.value.doc;
    if (!doc?.paths) return [];

    const byGroup = new Map<string, EndpointEntry[]>();
    for (const [path, methods] of Object.entries(doc.paths)) {
      for (const method of HTTP_METHODS) {
        const operation = methods[method];
        if (!operation) continue;
        const key = groupKey(path);
        if (!byGroup.has(key)) byGroup.set(key, []);
        byGroup.get(key)!.push({ path, method, operation });
      }
    }

    return [...byGroup.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, endpoints]) => ({
        name,
        endpoints: endpoints.sort(
          (x, y) => x.path.localeCompare(y.path) || x.method.localeCompare(y.method)
        ),
      }));
  });

  const endpointCount = computed(() =>
    groups.value.reduce((sum, g) => sum + g.endpoints.length, 0)
  );

  onMounted(() => loadSpec('v2'));
</script>

<template>
  <article class="mx-auto w-full max-w-4xl px-5 pb-16 sm:px-8">
    <!-- Back link -->
    <router-link
      to="/"
      class="inline-flex items-center gap-2 pt-2 font-mono text-[11px] uppercase tracking-[0.08em]
        text-day-ink-faint transition-colors hover:text-brand-600
        dark:text-night-ink-faint dark:hover:text-brand-500">
      <span aria-hidden="true">&larr;</span>
      {{ t('web.docs.back') }}
    </router-link>

    <!-- Header -->
    <header class="mt-8 border-b border-day-line pb-8 dark:border-white/[0.07]">
      <p class="mb-3 font-mono text-[10px] uppercase tracking-[0.1em] text-brand-600 dark:text-brand-500">
        {{ t('web.docs.eyebrow') }}
      </p>
      <h1 class="font-brand text-[34px] font-bold leading-[1.15] text-day-ink dark:text-night-ink sm:text-[40px]">
        {{ t('web.docs.title') }}
      </h1>
      <p class="mt-4 max-w-2xl text-[15px] font-light leading-[1.7] text-day-ink-muted dark:text-night-ink-muted">
        {{ t('web.docs.intro') }}
      </p>

      <!-- Intro facts: base URL + auth -->
      <dl class="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="border border-day-line bg-day-100 px-4 py-3 dark:border-white/[0.07] dark:bg-night-100">
          <dt class="font-mono text-[10px] uppercase tracking-[0.1em] text-day-ink-faint dark:text-night-ink-faint">
            {{ t('web.docs.base_url_label') }}
          </dt>
          <dd class="mt-1 break-all font-mono text-[13px] text-day-ink dark:text-night-ink">
            {{ baseUrl }}
          </dd>
        </div>
        <div class="border border-day-line bg-day-100 px-4 py-3 dark:border-white/[0.07] dark:bg-night-100">
          <dt class="font-mono text-[10px] uppercase tracking-[0.1em] text-day-ink-faint dark:text-night-ink-faint">
            {{ t('web.docs.auth_label') }}
          </dt>
          <dd class="mt-1 text-[12.5px] leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
            {{ t('web.docs.auth_note') }}
          </dd>
        </div>
      </dl>
    </header>

    <!-- Version tabs -->
    <div
      class="mt-8 flex flex-wrap items-center gap-2"
      role="tablist"
      :aria-label="t('web.docs.tab_aria')">
      <button
        v-for="v in VERSIONS"
        :key="v.id"
        type="button"
        role="tab"
        :aria-selected="selected === v.id"
        class="inline-flex items-baseline gap-2 border px-4 py-2 font-mono text-[12px] uppercase
          tracking-[0.06em] transition-colors"
        :class="
          selected === v.id
            ? 'border-brand-600 bg-brand-600 text-white dark:border-brand-500 dark:bg-brand-500'
            : 'border-day-line text-day-ink-muted hover:border-brand-400 hover:text-brand-600 dark:border-white/[0.12] dark:text-night-ink-muted dark:hover:text-brand-400'
        "
        @click="selectVersion(v.id)">
        {{ v.id }}
        <span
          class="text-[10px] tracking-[0.08em]"
          :class="selected === v.id ? 'text-white/75' : 'text-day-ink-faint dark:text-night-ink-faint'">
          {{ statusLabel(v.status) }}
        </span>
      </button>
    </div>

    <!-- alpha notice -->
    <p
      v-if="currentMeta.status === 'alpha'"
      class="mt-4 border border-amber-300/60 bg-amber-50 px-4 py-2.5 font-mono text-[11px]
        leading-relaxed text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
      {{ t('web.docs.alpha_note') }}
    </p>

    <!-- Spec header: description + download -->
    <div class="mt-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <p
        v-if="current.doc?.info?.description"
        class="max-w-2xl text-[13px] font-light leading-[1.65] text-day-ink-muted dark:text-night-ink-muted">
        {{ current.doc.info.description }}
      </p>
      <a
        :href="currentUrl"
        download
        class="inline-flex shrink-0 items-center gap-2 self-start border border-day-line px-4 py-2
          font-mono text-[11px] uppercase tracking-[0.06em] text-day-ink transition-colors
          hover:border-brand-400 hover:text-brand-600 dark:border-white/[0.12] dark:text-night-ink
          dark:hover:text-brand-400">
        <svg
          class="size-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line
          x1="12"
          y1="15"
          x2="12"
          y2="3" />
        </svg>
        {{ t('web.docs.download') }}
      </a>
    </div>

    <!-- Loading -->
    <div
      v-if="current.status === 'loading' || current.status === 'idle'"
      class="mt-10 flex items-center gap-3 font-mono text-[12px] text-day-ink-faint dark:text-night-ink-faint"
      data-testid="docs-loading">
      <svg
        class="size-4 animate-spin"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true">
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="3" />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 0 1 8-8v3a5 5 0 0 0-5 5H4z" />
      </svg>
      {{ t('web.docs.loading') }}
    </div>

    <!-- Error -->
    <div
      v-else-if="current.status === 'error'"
      class="mt-10 border border-rose-300/60 bg-rose-50 px-5 py-4 dark:border-rose-500/30 dark:bg-rose-500/10"
      data-testid="docs-error">
      <p class="font-mono text-[12px] font-semibold uppercase tracking-[0.08em] text-rose-700 dark:text-rose-300">
        {{ t('web.docs.error_title') }}
      </p>
      <p class="mt-1 text-[13px] text-day-ink-muted dark:text-night-ink-muted">
        {{ t('web.docs.error_body') }}
      </p>
      <button
        type="button"
        class="mt-3 inline-flex items-center border border-day-line px-3 py-1.5 font-mono text-[11px]
          uppercase tracking-[0.06em] text-day-ink transition-colors hover:bg-day-100
          dark:border-white/[0.12] dark:text-night-ink dark:hover:bg-night-100"
        @click="loadSpec(selected)">
        {{ t('web.docs.retry') }}
      </button>
    </div>

    <!-- Endpoints -->
    <div
      v-else-if="current.status === 'loaded'"
      class="mt-8"
      data-testid="docs-endpoints">
      <p class="mb-5 font-mono text-[10px] uppercase tracking-[0.1em] text-day-ink-faint dark:text-night-ink-faint">
        {{ t('web.docs.endpoint_count', { count: endpointCount }) }}
      </p>

      <p
        v-if="groups.length === 0"
        class="text-[13px] text-day-ink-muted dark:text-night-ink-muted">
        {{ t('web.docs.no_endpoints') }}
      </p>

      <section
        v-for="group in groups"
        :key="group.name"
        class="mb-8">
        <h2 class="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 dark:text-brand-500">
          {{ group.name }}
        </h2>
        <div class="border border-day-line dark:border-white/[0.07]">
          <ApiEndpoint
            v-for="ep in group.endpoints"
            :key="ep.method + ' ' + ep.path"
            :path="ep.path"
            :method="ep.method"
            :operation="ep.operation" />
        </div>
      </section>
    </div>
  </article>
</template>
